import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Spinner, Alert, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFilePdf, faSync, faFilter } from "@fortawesome/free-solid-svg-icons";
import * as XLSX from "xlsx-js-style";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getGroupConsignments } from "../../../api/warehouse/groupConsignmentApi";
import { getInvoiceDetailsApi } from "../../../api/report/reportApi";
import { baseUrl } from "../../../api";
import "./OrderDeliveryHistoryReport.css";

/* ------------------------------------------------------------------ */
/* Types & helpers                                                     */
/* ------------------------------------------------------------------ */

interface ReportRow {
  id: string;
  groupKey: string;
  group: any;
  consignment: any;
  item: any;
  invoiceDetail: any;
  hay: string;
  hayN: string;
  isGroupStart: boolean;
   groupTotalQty?: number;
}

type FilterKind = "list" | "number" | "date" | "none";

interface Column {
  key: string;
  label: string;
  width: number;
  align?: "left" | "right" | "center";
  filter?: FilterKind;
  text: (r: ReportRow, index: number) => string;
  num?: (r: ReportRow) => number | null;
  date?: (r: ReportRow) => Date | null;
  render?: (r: ReportRow, index: number) => React.ReactNode;
  mergeGroup?: boolean; // If true, this column should be merged for same group
}

const publicApiUrl = (path: string, params: Record<string, string>): string | null => {
  try {
    const base = String(baseUrl || "").trim().replace(/\/+$/, "");
    if (!base) return null;
    const url = new URL(`${base}/${path.replace(/^\/+/, "")}`, window.location.origin);
    Object.entries(params).forEach(([key, value]) => url.searchParams.set(key, value));
    return url.toString();
  } catch {
    return null;
  }
};

interface ColFilter {
  list?: string[];
  min?: string;
  max?: string;
  from?: string;
  to?: string;
}

const ROW_H = 24;
const OVERSCAN = 12;

const STATUS_TEXT: Record<string, string> = {
  "0": "Ordered",
  "1": "In Progress",
  "2": "Delivered",
  "3": "Out of Delivery",
  "4": "Forced Delivery",
};

const STATUS_CLASS: Record<string, string> = {
  "0": "st-info",
  "1": "st-primary",
  "2": "st-success",
  "3": "st-warning",
  "4": "st-danger",
};

const OTP_STATUS_TEXT: Record<string, string> = {
  "verified": "Verified",
  "force_delivered": "Force Delivered",
  "pending": "Pending",
};

const OTP_STATUS_CLASS: Record<string, string> = {
  "verified": "st-success",
  "force_delivered": "st-danger",
  "pending": "st-warning",
};

const toNum = (v: any): number | null => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return isNaN(n) ? null : n;
};

const toDate = (d?: string): Date | null => {
  if (!d) return null;
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? null : dt;
};
const pickDate = (d?: string) => (d && !String(d).startsWith("0001-01-01") ? d : undefined);
const itemDate = (r: ReportRow) => r.item?.dPostingDate || r.item?.dInvoiceDate;
const groupFreight = (r: ReportRow) => toNum(r.group?.aFreightAmount) || 0;
const perConsignmentFreight = (r: ReportRow) =>
  groupFreight(r) / (r.group?.tConsignments?.length || 1);
const fmtMoney = (n: number) =>
  n ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "";
// local YYYY-MM-DD (avoids UTC off-by-one)
const ymd = (d: string) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");
const fmtDateTime = (d?: string) => (d ? new Date(d).toLocaleString("en-GB").replace(", ", " ") : "-");
const statusText = (s: any) => STATUS_TEXT[String(s)] || "Unknown";
const otpStatusText = (s: any) => OTP_STATUS_TEXT[String(s)] || s || "Pending";
const otpKey = (c: any): string => {
  if (c?.sOtpStatus) return String(c.sOtpStatus);
  if (String(c?.eStatus) === "4") return "force_delivered";
  if (String(c?.eStatus) === "2") return "verified";
  return "pending";
};
const splitTerms = (s: string) =>
  s
    .split(/[,;|\r\n\t]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9\u0001]/g, "");

const isActive = (f: ColFilter) =>
  !!((f.list && f.list.length) || f.min || f.max || f.from || f.to);

/* ------------------------------------------------------------------ */
/* Columns                                                             */
/* ------------------------------------------------------------------ */

const COLUMNS: Column[] = [
  { key: "sr", label: "Sr", width: 60, align: "center", filter: "none", text: (_r, i) => String(i + 1) },
  { key: "groupConsignmentNo", label: "Group Consignment No", width: 160, text: (r) => r.group?.grpConsignmentID || "", mergeGroup: true },
  { key: "consignmentNo", label: "Consignment Number", width: 160, text: (r) => r.consignment?.sConsignmentNo || "" },
  { key: "dispatchLocation", label: "Dispatch Location", width: 150, text: (r) => r.consignment?.tWarehouse?.sName || "" },
  { key: "partyName", label: "Party Name", width: 180, text: (r) => r.invoiceDetail?.BilltoName || r.item?.sBilltoName || "" },
  { key: "town", label: "Town", width: 130, text: (r) => r.invoiceDetail?.Billtocity || r.item?.sBilltocity || "" },
  { key: "invoiceSrNo", label: "Invoice Sr. Number", width: 150, text: (r) => r.invoiceDetail?.Document_No || r.item?.sDocumentNo || "" },
    {
    key: "invoiceDate",
    label: "Invoice Date",
    width: 120,
    align: "center",
    filter: "date",
    text: (r) => {
      const d = r.invoiceDetail?.Posting_Date || itemDate(r);
      return d ? fmtDate(d) : "";
    },
    date: (r) => toDate(r.invoiceDetail?.Posting_Date || itemDate(r)),
  },
    {
    key: "weight",
    label: "Weight",
    width: 100,
    align: "right",
    filter: "number",
    text: (r) => String(r.invoiceDetail?.totalQty ?? r.consignment?.aQuantity ?? ""),
    num: (r) => toNum(r.invoiceDetail?.totalQty ?? r.consignment?.aQuantity),
  },
    {
    key: "totalWeight",
    label: "Total Weight",
    width: 110,
    align: "right",
    filter: "number",
    text: (r) => (r.groupTotalQty ? String(r.groupTotalQty) : ""),
    num: (r) => r.groupTotalQty ?? null,
    mergeGroup: true,
  },
  {
    key: "totalKms",
    label: "Total KMS",
    width: 100,
    align: "right",
    filter: "number",
    text: (r) => "",
    num: () => null,
    mergeGroup: true,
  },
  { key: "ewayBillNo", label: "E-Way Bill Number", width: 150, text: (r) => r.invoiceDetail?.Eway_bill_no || "" },
    {
    key: "ewayBillDate",
    label: "E-Way Bill Date",
    width: 120,
    align: "center",
    filter: "date",
    text: (r) => {
      const d = pickDate(r.invoiceDetail?.Eway_bill_date);
      return d ? fmtDate(d) : "";
    },
    date: (r) => toDate(pickDate(r.invoiceDetail?.Eway_bill_date)),
  },
    { key: "transportName", label: "Transport Name", width: 150, text: (r) => r.invoiceDetail?.Transport_Name || r.group?.tTransport?.sName || "" },
{ key: "transportCoordinatorNo", label: "Transport Coordinator No", width: 160, text: (r) => r.group?.tAssignDriver?.sTransportCoordinatorNo || r.consignment?.tAssignDriver?.sTransportCoordinatorNo || "" },
  { key: "vehicleNumber", label: "Vehicle Number", width: 130, text: (r) => r.group?.tVehicle?.sRegNo || "" },
    {
    key: "otpVerification",
    label: "OTP Verification",
    width: 130,
    align: "center",
    text: (r) => otpStatusText(otpKey(r.consignment)),
    render: (r) => (
      <span className={`erp-pill ${OTP_STATUS_CLASS[otpKey(r.consignment)] || "st-secondary"}`}>
        {otpStatusText(otpKey(r.consignment))}
      </span>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: 140,
    align: "center",
    text: (r) => (r.consignment ? statusText(r.consignment.eStatus) : ""),
    render: (r) =>
      r.consignment ? (
        <span className={`erp-pill ${STATUS_CLASS[String(r.consignment.eStatus)] || "st-secondary"}`}>
          {statusText(r.consignment.eStatus)}
        </span>
      ) : (
        "-"
      ),
  },
  {
    key: "deliveryDate",
    label: "Delivery Date",
    width: 120,
    align: "center",
    filter: "date",
    text: (r) => (r.consignment?.dDeliveryDate ? fmtDate(r.consignment.dDeliveryDate) : ""),
    date: (r) => toDate(r.consignment?.dDeliveryDate),
  },
    {
    key: "totalFreight",
    label: "Total Freight",
    width: 120,
    align: "right",
    filter: "number",
    text: (r) => fmtMoney(groupFreight(r)),
    num: (r) => groupFreight(r),
    mergeGroup: true,
  },
    {
    key: "freight",
    label: "Freight",
    width: 110,
    align: "right",
    filter: "number",
    text: (r) => fmtMoney(perConsignmentFreight(r)),
    num: (r) => perConsignmentFreight(r),
  },
  {
    key: "podPhoto",
    label: "POD Photo",
    width: 130,
    text: (r) => (r.consignment?.sProveOfDeliveryURLs?.length ? "View POD" : "-"),
    render: (r) => {
      const invoiceNo = r.invoiceDetail?.Document_No || r.item?.sDocumentNo;
      const consignmentNo = r.consignment?.sConsignmentNo;

      if (!r.consignment?.sProveOfDeliveryURLs?.length) {
        return "-";
      }

      let podUrl;
      // Prefer invoice number since we're in a report showing invoice data
      if (invoiceNo) {
        podUrl = publicApiUrl("public/pod", { invoiceNo: String(invoiceNo), index: "0" });
      } else if (consignmentNo) {
        podUrl = publicApiUrl("public/pod", { consignmentNo: String(consignmentNo), index: "0" });
      } else {
        return "-";
      }
      if (!podUrl) return "-";

      return (
        <a href={podUrl} target="_blank" rel="noopener noreferrer" className="link-blue">
          View POD
        </a>
      );
    },
  },
  {
    key: "temperatureReport",
    label: "Data Logger",
    width: 150,
    text: (r) => "View Report",
    mergeGroup: true,
    render: (r) => {
      const groupConsignmentNo = r.group?.grpConsignmentID;

      if (!groupConsignmentNo) {
        return "-";
      }

      const reportUrl = publicApiUrl("public/pod/temperature-report", { groupConsignmentNo: String(groupConsignmentNo) });
      if (!reportUrl) return "-";

      return (
        <a href={reportUrl} target="_blank" rel="noopener noreferrer" className="link-blue">
          View Report
        </a>
      );
    },
  },
];

const COL_BY_KEY: Record<string, Column> = COLUMNS.reduce<Record<string, Column>>((acc, c) => {
  acc[c.key] = c;
  return acc;
}, {});

const defaultWidths = () =>
  COLUMNS.reduce<Record<string, number>>((acc, c) => {
    acc[c.key] = c.width;
    return acc;
  }, {});

const buildHay = (r: ReportRow) =>
  COLUMNS.filter((c) => c.key !== "sr")
    .map((c) => c.text(r, 0))
    .join("\u0001")
    .toLowerCase();

/* ------------------------------------------------------------------ */
/* Filter popover                                                      */
/* ------------------------------------------------------------------ */

interface PopoverProps {
  col: Column;
  anchor: DOMRect;
  candidates: ReportRow[];
  filter: ColFilter;
  onChange: (f: ColFilter) => void;
  onClear: () => void;
  onClose: () => void;
}

const POPOVER_W = 280;

const FilterPopover: React.FC<PopoverProps> = ({ col, anchor, candidates, filter, onChange, onClear, onClose }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const kind: FilterKind = col.filter || "list";

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (ref.current && ref.current.contains(t)) return;
      if (t.closest && t.closest("[data-filter-btn]")) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onScroll = (e: Event) => {
      if (ref.current && e.target instanceof Node && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const selected = filter.list;
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    if (kind === "list") {
      candidates.forEach((r) => {
        const v = col.text(r, 0);
        m.set(v, (m.get(v) || 0) + 1);
      });
      (selected || []).forEach((v) => {
        if (!m.has(v)) m.set(v, 0);
      });
    }
    return m;
  }, [candidates, col, kind, selected]);

  const shownValues = useMemo(() => {
    const all = Array.from(counts.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const terms = splitTerms(search);
    if (!terms.length) return all;
    return all.filter((v) => {
      const label = (v || "(blank)").toLowerCase();
      return terms.some((t) => label.indexOf(t) !== -1);
    });
  }, [counts, search]);

  const sel = selected || [];
  const toggle = (v: string) =>
    onChange({ ...filter, list: sel.indexOf(v) !== -1 ? sel.filter((s) => s !== v) : [...sel, v] });
  const selectAllShown = () => {
    const merged = [...sel];
    shownValues.forEach((v) => {
      if (merged.indexOf(v) === -1) merged.push(v);
    });
    onChange({ ...filter, list: merged });
  };

  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - POPOVER_W - 8));
  const top = Math.min(anchor.bottom + 2, Math.max(8, window.innerHeight - 440));

  return (
    <div className="fp" ref={ref} style={{ left, top, width: POPOVER_W }}>
      <div className="fp-title">{col.label}</div>

      {kind === "list" && (
        <>
          <input
            className="fp-search"
            autoFocus
            placeholder="Search — separate several values with , or ;"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                selectAllShown();
              }
            }}
          />
          <div className="fp-actions">
            <button type="button" onClick={selectAllShown}>
              Select all shown ({shownValues.length})
            </button>
            <button type="button" onClick={() => onChange({ ...filter, list: [] })}>
              Unselect
            </button>
          </div>
          <div className="fp-list">
            {shownValues.slice(0, 300).map((v) => (
              <label key={v || "__blank"} className="fp-opt" title={v}>
                <input type="checkbox" checked={sel.indexOf(v) !== -1} onChange={() => toggle(v)} />
                <span className="fp-opt-text">{v || "(Blank)"}</span>
                <span className="fp-opt-count">{counts.get(v)}</span>
              </label>
            ))}
            {shownValues.length === 0 && <div className="fp-empty">No matching values</div>}
            {shownValues.length > 300 && <div className="fp-empty">Showing first 300. Type to narrow the list.</div>}
          </div>
          <div className="fp-note">Tick the values to show. Nothing ticked = no filter.</div>
        </>
      )}

      {(kind === "number") && (
        <>
          <div className="fp-row">
            <label>Min</label>
            <input type="number" value={filter.min || ""} onChange={(e) => onChange({ ...filter, min: e.target.value })} />
          </div>
          <div className="fp-row">
            <label>Max</label>
            <input type="number" value={filter.max || ""} onChange={(e) => onChange({ ...filter, max: e.target.value })} />
          </div>
        </>
      )}

      {kind === "date" && (
        <>
          <div className="fp-row">
            <label>From</label>
            <input type="date" value={filter.from || ""} onChange={(e) => onChange({ ...filter, from: e.target.value })} />
          </div>
          <div className="fp-row">
            <label>To</label>
            <input type="date" value={filter.to || ""} onChange={(e) => onChange({ ...filter, to: e.target.value })} />
          </div>
        </>
      )}

      <div className="fp-foot">
        <button type="button" className="fp-clear" onClick={onClear}>
          Clear filter
        </button>
        <button type="button" className="fp-done" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/* Searchable select                                                   */
/* ------------------------------------------------------------------ */

interface SearchableSelectProps {
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
  width?: number;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select...",
  width = 250,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // matches normal text and also ignores dashes/spaces, e.g. "grp001" finds "GRP-001"
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    const qn = squash(q);
    return options.filter((o) => o.toLowerCase().includes(q) || squash(o).includes(qn));
  }, [options, query]);

  // close on outside click
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // keep highlighted item visible while using arrow keys
  useEffect(() => {
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const openMenu = () => {
    setQuery("");
    setActive(0);
    setOpen(true);
  };

  const toggle = (v: string) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v));
    } else {
      onChange([...value, v]);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[active] !== undefined) toggle(filtered[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="ss-wrap" ref={wrapRef} style={{ width }}>
      <input
        className="ss-input"
        placeholder={placeholder}
        // shows the typed text while open, otherwise the selected values
        value={open ? query : value.join(", ")}
        onFocus={openMenu}
        onClick={() => !open && openMenu()}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          if (!open) setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      {value.length > 0 && !open && (
        <button type="button" className="ss-clear" aria-label="Clear selection" onClick={() => onChange([])}>
          ×
        </button>
      )}
      {open && (
        <div className="ss-menu" ref={listRef}>
          {filtered.slice(0, 200).map((o, i) => (
            <div
              key={o}
              className={`ss-opt ${i === active ? "active" : ""} ${value.includes(o) ? "selected" : ""}`}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                e.preventDefault(); // keep input focus so blur doesn't swallow the click
                toggle(o);
              }}
            >
              <span className="ss-opt-check">{value.includes(o) ? "✓ " : "  "}</span>
              {o}
            </div>
          ))}
          {filtered.length === 0 && <div className="ss-empty">No matching group consignment</div>}
          {filtered.length > 200 && <div className="ss-empty">Showing first 200. Type to narrow.</div>}
        </div>
      )}
    </div>
  );
};
const OrderDeliveryHistoryReport: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [widths, setWidths] = useState<Record<string, number>>(defaultWidths);

  const [filters, setFilters] = useState<Record<string, ColFilter>>({});
  const [globalSearch, setGlobalSearch] = useState("");
  const [openFilter, setOpenFilter] = useState<{ key: string; rect: DOMRect } | null>(null);

  // Filter form state
  const [selectedGroupConsignments, setSelectedGroupConsignments] = useState<string[]>([]);
  const [deliveryDateFrom, setDeliveryDateFrom] = useState("");
  const [deliveryDateTo, setDeliveryDateTo] = useState("");
  const [groupConsignments, setGroupConsignments] = useState<any[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewH, setViewH] = useState(600);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => setViewH(el.clientHeight);
    update();
    const RO = (window as any).ResizeObserver;
    const ro = RO ? new RO(update) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  /* ---------- data ---------- */
  const fetchGroupConsignments = useCallback(async () => {
    try {
      const groupRes = await getGroupConsignments();
      const groups: any[] = groupRes.data || [];
      setGroupConsignments(groups);
    } catch (e) {
      console.error("Error fetching group consignments:", e);
      setError("Failed to load group consignments. Please try again later.");
    }
  }, []);

 const fetchData = useCallback(async () => {
  setLoading(true);
  setError(null);
  try {
    // Get selected groups or all if none selected
    const groupsToProcess = selectedGroupConsignments.length > 0
      ? groupConsignments.filter((g) => selectedGroupConsignments.includes(g.grpConsignmentID))
      : groupConsignments;

    if (!groupsToProcess.length) { setRows([]); return; }

    const allRows: ReportRow[] = [];
    let groupNo = 0;

    for (const selectedGroup of groupsToProcess) {
      const consignments: any[] = selectedGroup.tConsignments || [];
      if (!consignments.length) continue;

      // dPostingDate is the real field in your API (not dInvoiceDate)
      const dates = Array.from(
        new Set(
          consignments
            .map((c) => c.tItems?.[0]?.dPostingDate || c.tItems?.[0]?.dInvoiceDate)
            .filter(Boolean)
            .map((d: string) => ymd(d))
        )
      );
      const locationCode =
        selectedGroup.tWarehouse?.sLocationCode ||
        consignments[0]?.tWarehouse?.sLocationCode;

      const invoiceMap = new Map<string, any>();

      if (dates.length && locationCode) {
        const responses = await Promise.all(
          dates.map((d) => getInvoiceDetailsApi({ Posting_Date: d, Location_Code: `'${locationCode}'` }))
        );

        responses.forEach((res: any) => {
          const body = res?.data;
          const lines: any[] = body?.value ?? body?.data?.value ?? [];
          lines.forEach((l) => {
            const isRoundOff = String(l.Description || "").toLowerCase() === "round off";
            const ex = invoiceMap.get(l.Document_No);
            if (!ex) {
              invoiceMap.set(l.Document_No, { ...l, totalQty: isRoundOff ? 0 : Number(l.Quantity) || 0 });
            } else if (!isRoundOff) {
              ex.totalQty += Number(l.Quantity) || 0;
            }
          });
        });
      }

      const flat: ReportRow[] = consignments.map((consignment, ci) => {
        const item = consignment?.tItems?.[0] || null;
        return {
          id: `${selectedGroup.grpConsignmentID}-${ci}`,
          groupKey: selectedGroup.grpConsignmentID,
          group: selectedGroup,
          consignment,
          item,
          invoiceDetail: invoiceMap.get(item?.sDocumentNo) || null,
          hay: "",
          hayN: "",
          isGroupStart: ci === 0,
        };
      });

      // Match the Weight column's invoice quantity / consignment quantity fallback.
      const groupTotalQty = flat.reduce(
        (sum, row) => sum + (toNum(row.invoiceDetail?.totalQty ?? row.consignment?.aQuantity) ?? 0),
        0
      );
      flat.forEach((r) => { r.groupTotalQty = groupTotalQty; });

      allRows.push(...flat);
      groupNo++;
    }

    setRows(allRows);
  } catch (e) {
    console.error("Error fetching data:", e);
    setError("Failed to load data. Please try again later.");
    setRows([]);
  } finally {
    setLoading(false);
  }
}, [selectedGroupConsignments, groupConsignments]);
  useEffect(() => {
    fetchGroupConsignments();
  }, [fetchGroupConsignments]);

  /* ---------- filtering ---------- */
  const globalTerms = useMemo(() => splitTerms(globalSearch), [globalSearch]);

  const rowPasses = useCallback(
    (r: ReportRow, skipKey?: string) => {
      if (globalTerms.length) {
        if (!r.hay) {
          r.hay = buildHay(r);
          r.hayN = squash(r.hay);
        }
        const ok = globalTerms.some((t) => {
          if (r.hay.indexOf(t) !== -1) return true;
          const tn = squash(t);
          return tn.length > 0 && r.hayN.indexOf(tn) !== -1;
        });
        if (!ok) return false;
      }

      for (const key of Object.keys(filters)) {
        if (key === skipKey) continue;
        const f = filters[key];
        const c = COL_BY_KEY[key];
        if (!c) continue;

        if (f.list && f.list.length && f.list.indexOf(c.text(r, 0)) === -1) return false;

        if (f.min || f.max) {
          const n = c.num ? c.num(r) : null;
          const lo = toNum(f.min);
          const hi = toNum(f.max);
          if (n === null) return false;
          if (lo !== null && n < lo) return false;
          if (hi !== null && n > hi) return false;
        }

        if (f.from || f.to) {
          const d = c.date ? c.date(r) : null;
          if (!d) return false;
          if (f.from && d < new Date(`${f.from}T00:00:00`)) return false;
          if (f.to && d > new Date(`${f.to}T23:59:59`)) return false;
        }
      }
      return true;
    },
    [filters, globalTerms]
  );

  const filteredRows = useMemo(() => rows.filter((r) => rowPasses(r)), [rows, rowPasses]);
// for every filtered row: index of the last row of its group (contiguous rows with same groupKey)
const groupEnd = useMemo(() => {
  const n = filteredRows.length;
  const arr: number[] = new Array(n);
  for (let i = n - 1; i >= 0; i--) {
    arr[i] =
      i === n - 1 || filteredRows[i + 1].groupKey !== filteredRows[i].groupKey ? i : arr[i + 1];
  }
  return arr;
}, [filteredRows]);
  const openCandidates = useMemo(
    () => (openFilter ? rows.filter((r) => rowPasses(r, openFilter.key)) : []),
    [openFilter, rows, rowPasses]
  );

  const setColFilter = (key: string, f: ColFilter) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (isActive(f)) next[key] = f;
      else delete next[key];
      return next;
    });
  };

  const clearAll = () => {
    setFilters({});
    setGlobalSearch("");
    setOpenFilter(null);
    setSelectedGroupConsignments([]);
    setDeliveryDateFrom("");
    setDeliveryDateTo("");
    setRows([]);
  };

  // Calculate row spans for merged columns
  const getRowSpan = (row: ReportRow, rows: ReportRow[], rowIndex: number) => {
    if (!row.isGroupStart) return 1;
    
    // Count consecutive rows with same groupKey
    let span = 1;
    for (let i = rowIndex + 1; i < rows.length; i++) {
      if (rows[i].groupKey === row.groupKey) {
        span++;
      } else {
        break;
      }
    }
    return span;
  };

  const closeFilter = useCallback(() => setOpenFilter(null), []);

  const toggleFilter = (e: React.MouseEvent<HTMLButtonElement>, key: string) => {
    e.stopPropagation();
    if (openFilter && openFilter.key === key) {
      setOpenFilter(null);
    } else {
      setOpenFilter({ key, rect: e.currentTarget.getBoundingClientRect() });
    }
  };

  const summarize = (f: ColFilter) => {
    const parts: string[] = [];
    if (f.list && f.list.length) parts.push(f.list.length === 1 ? f.list[0] || "(Blank)" : `${f.list.length} values`);
    if (f.min || f.max) parts.push(`${f.min || "…"} to ${f.max || "…"}`);
    if (f.from || f.to) parts.push(`${f.from || "…"} → ${f.to || "…"}`);
    return parts.join("; ");
  };

  const activeKeys = Object.keys(filters);

  /* ---------- column resize ---------- */
  const startResize = (e: React.MouseEvent, key: string) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = widths[key];
    const onMove = (ev: MouseEvent) => {
      setWidths((w) => ({ ...w, [key]: Math.max(60, startW + ev.clientX - startX) }));
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      document.body.classList.remove("erp-resizing");
    };
    document.body.classList.add("erp-resizing");
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  const resetColumnWidth = (key: string) => {
    const def = COLUMNS.find((c) => c.key === key)?.width || 100;
    setWidths((w) => ({ ...w, [key]: def }));
  };

  const totalWidth = COLUMNS.reduce((sum, c) => sum + (widths[c.key] || c.width), 0);

  /* ---------- virtual window ---------- */
  const total = filteredRows.length;
  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(total, Math.ceil((scrollTop + viewH) / ROW_H) + OVERSCAN);
  const visibleRows = filteredRows.slice(start, end);
  const topPad = start * ROW_H;
  const bottomPad = (total - end) * ROW_H;

  /* ---------- export ---------- */
  const buildMatrix = () => ({
    head: COLUMNS.map((c) => c.label),
    body: filteredRows.map((r, i) => COLUMNS.map((c) => c.text(r, i))),
  });
  // For each visible row: number of rows to merge if it starts a group, else 0.
  // Based on the filtered rows, so merging stays correct while filtering.
  const getSpans = (list: ReportRow[]) => {
    const spans: number[] = list.map(() => 0);
    let i = 0;
    while (i < list.length) {
      let j = i + 1;
      while (j < list.length && list[j].groupKey === list[i].groupKey) j++;
      spans[i] = j - i;
      i = j;
    }
    return spans;
  };
  const stamp = () => new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");

   const exportToExcel = () => {
    if (!filteredRows.length) return;

    const FONT = "Calibri";
    const thin = { style: "thin", color: { rgb: "000000" } };
    const medium = { style: "medium", color: { rgb: "000000" } };
    const spans = getSpans(filteredRows);
    const toSerial = (d: Date) => (d.getTime() - d.getTimezoneOffset() * 60000) / 86400000 + 25569;

    // Excel columns in the exact order of your required format
    const XL: { key: string; label: string; width: number; merge?: boolean; align?: "left" | "center" | "right" }[] = [
      { key: "grpSr", label: "Consigment Number", width: 18, merge: true, align: "center" },
      { key: "dispatchLocation", label: "Dispatch Location", width: 18 },
      { key: "partyName", label: "Party Name", width: 28 },
      { key: "town", label: "Town", width: 16 },
      { key: "invoiceSrNo", label: "Invoice Sr. Number", width: 22, align: "right" },
      { key: "invoiceDate", label: "Invoice Date", width: 14, align: "right" },
      { key: "weight", label: "Weight", width: 10, align: "right" },
      { key: "totalWeight", label: "Total Weight", width: 14, merge: true, align: "center" },
      { key: "totalKms", label: "Total KMS", width: 12, merge: true, align: "center" },
      { key: "ewayBillNo", label: "E-Way Bill Number", width: 20 },
      { key: "ewayBillDate", label: "E-Way Bill Date", width: 16, align: "right" },
      { key: "transportName", label: "Transport Name", width: 20 },
      { key: "transportCoordinatorNo", label: "Transport Coordinator No", width: 22 },
      { key: "vehicleNumber", label: "Vehicle Number", width: 18 },
      { key: "otpVerification", label: "OTP Verification", width: 18, align: "center" },
      { key: "status", label: "Status", width: 16, align: "center" },
      { key: "deliveryDate", label: "Delivery Date", width: 14, align: "right" },
      { key: "totalFreight", label: "Total Freight", width: 14, merge: true, align: "center" },
      { key: "freight", label: "Freight", width: 12, align: "right" },
      { key: "podPhoto", label: "POD Photo", width: 20 },
      { key: "temperatureReport", label: "Data Logger", width: 20, merge: true, align: "center" },
    ];

    const numFmt: Record<string, string> = {
      weight: "General",
      totalWeight: "General",
      totalKms: "General",
      totalFreight: "#,##0.00",
      freight: "#,##0.00",
    };
    const dateFmt: Record<string, string> = {
      invoiceDate: "dd/mm/yyyy",
      ewayBillDate: "dd/mm/yyyy",
      deliveryDate: "dd/mm/yyyy",
    };

    // link for a row/column (null = no link)
    const linkFor = (r: ReportRow, key: string): string | null => {
      if (key === "podPhoto" && r.consignment?.sProveOfDeliveryURLs?.length) {
        const invoiceNo = r.invoiceDetail?.Document_No || r.item?.sDocumentNo;
        const consignmentNo = r.consignment?.sConsignmentNo;
        if (invoiceNo) return publicApiUrl("public/pod", { invoiceNo: String(invoiceNo), index: "0" });
        if (consignmentNo) return publicApiUrl("public/pod", { consignmentNo: String(consignmentNo), index: "0" });
        return null;
      }
      if (key === "temperatureReport") {
        const g = r.group?.grpConsignmentID;
        return g ? publicApiUrl("public/pod/temperature-report", { groupConsignmentNo: String(g) }) : null;
      }
      return null;
    };

    const ws: any = {};
    const put = (r: number, c: number, cell: any) => {
      ws[XLSX.utils.encode_cell({ r, c })] = cell;
    };
    const lastCol = XL.length - 1;

    // ---- header row (row 0, no title/info rows, same as your sample) ----
    const headBorder = { top: thin, bottom: thin, left: thin, right: thin };
    XL.forEach((c, ci) =>
      put(0, ci, {
        t: "s",
        v: c.label,
        s: {
          font: { name: FONT, sz: 11, bold: true },
          fill: { fgColor: { rgb: "D9D9D9" } },
          alignment: { horizontal: "center", vertical: "center", wrapText: true },
          border: headBorder,
        },
      })
    );

    // ---- data rows ----
    const mergeRanges: any[] = [];
    let groupNo = 0;

    filteredRows.forEach((r, i) => {
      const row = i + 1;
      if (spans[i] > 0) groupNo++;
      const lastInGroup = i === filteredRows.length - 1 || filteredRows[i + 1].groupKey !== r.groupKey;
      const border = { top: thin, left: thin, right: thin, bottom: lastInGroup ? medium : thin };

      XL.forEach((c, ci) => {
        const style = (isLink = false) => ({
          font: {
            name: FONT,
            sz: 10,
            ...(isLink ? { color: { rgb: "0000FF" }, underline: true } : {}),
          },
          alignment: { horizontal: c.align || "left", vertical: "center", wrapText: false },
          border,
        });

        // rows covered by a merged cell: empty cell, only so borders draw
        if (c.merge && spans[i] === 0) {
          put(row, ci, { t: "s", v: "", s: style() });
          return;
        }
        if (c.merge && spans[i] > 1) {
          mergeRanges.push({ s: { r: row, c: ci }, e: { r: row + spans[i] - 1, c: ci } });
        }

        let cell: any;
        let isLink = false;

        if (c.key === "grpSr") {
          cell = { t: "n", v: groupNo };
        } else {
          const col = COL_BY_KEY[c.key];
          const n = col.num ? col.num(r) : null;
          const d = col.date ? col.date(r) : null;

          if (numFmt[c.key] && n !== null) {
            cell = { t: "n", v: n, z: numFmt[c.key] };
          } else if (dateFmt[c.key] && d) {
            cell = { t: "n", v: toSerial(d), z: dateFmt[c.key] };
          } else {
            let text = col.text(r, i);
            const url = linkFor(r, c.key);
            if (c.key === "podPhoto") text = url ? "Attached POD" : "";
            if (c.key === "temperatureReport") text = url ? "Attached PDF file" : "";
            cell = { t: "s", v: text };
            if (url) {
              cell.l = { Target: url, Tooltip: "Click to open" };
              isLink = true;
            }
          }
        }

        cell.s = style(isLink);
        put(row, ci, cell);
      });
    });

    ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: filteredRows.length, c: lastCol } });
    ws["!merges"] = mergeRanges;
    ws["!cols"] = XL.map((c) => ({ wch: c.width }));
    ws["!rows"] = [{ hpt: 24 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Order Delivery History");
    XLSX.writeFile(wb, `order_delivery_history_report_${stamp()}.xlsx`);
  };

   const exportToPDF = () => {
    if (!filteredRows.length) return;

    const spans = getSpans(filteredRows);

    // link for a given row/column (null = not a link)
    const linkFor = (r: ReportRow, key: string): string | null => {
      if (key === "podPhoto" && r.consignment?.sProveOfDeliveryURLs?.length) {
        const invoiceNo = r.invoiceDetail?.Document_No || r.item?.sDocumentNo;
        const consignmentNo = r.consignment?.sConsignmentNo;
        if (invoiceNo) return publicApiUrl("public/pod", { invoiceNo: String(invoiceNo), index: "0" });
        if (consignmentNo) return publicApiUrl("public/pod", { consignmentNo: String(consignmentNo), index: "0" });
        return null;
      }
      if (key === "temperatureReport") {
        const g = r.group?.grpConsignmentID;
        return g ? publicApiUrl("public/pod/temperature-report", { groupConsignmentNo: String(g) }) : null;
      }
      return null;
    };

    // body: merged columns get a rowSpan on the first row and are omitted on the others
    const body = filteredRows.map((r, i) => {
      const cells: any[] = [];
      COLUMNS.forEach((c) => {
        if (c.mergeGroup && spans[i] === 0) return; // covered by rowSpan above
        const cell: any = { content: c.text(r, i) || "-" };
        if (c.mergeGroup) {
          cell.styles = { valign: "middle" };
          if (spans[i] > 1) cell.rowSpan = spans[i];
        }
        cells.push(cell);
      });
      return cells;
    });

    const head = COLUMNS.map((c) => c.label);

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a3" });
    doc.setFontSize(14);
    doc.text("Order Delivery History Report", 30, 30);
    doc.setFontSize(9);
    doc.text(`Generated: ${new Date().toLocaleString("en-GB")}    Rows: ${filteredRows.length}`, 30, 46);

    const colStyles: Record<number, any> = {};
    COLUMNS.forEach((c, i) => {
      colStyles[i] = { halign: c.align || "left" };
    });

    autoTable(doc, {
      head: [head],
      body,
      startY: 56,
      styles: { fontSize: 6, cellPadding: 2.5, overflow: "linebreak", lineWidth: 0.3, lineColor: [200, 205, 210], valign: "middle" },
      headStyles: { fillColor: [41, 62, 88], textColor: 255, fontSize: 6, halign: "center" },
      alternateRowStyles: { fillColor: [246, 248, 250] },
      columnStyles: colStyles,
      margin: { left: 20, right: 20 },
      // blue text for link cells
      didParseCell: (data) => {
        if (data.section !== "body") return;
        const r = filteredRows[data.row.index];
        const key = COLUMNS[data.column.index]?.key;
        if (r && key && linkFor(r, key)) data.cell.styles.textColor = [0, 0, 255];
      },
      // clickable area = exactly the cell
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        const r = filteredRows[data.row.index];
        const key = COLUMNS[data.column.index]?.key;
        if (!r || !key) return;
        const url = linkFor(r, key);
        if (url) doc.link(data.cell.x, data.cell.y, data.cell.width, data.cell.height, { url });
      },
      didDrawPage: () => {
        const p = (doc as any).internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`Page ${p}`, doc.internal.pageSize.getWidth() - 60, doc.internal.pageSize.getHeight() - 14);
      },
    });
    doc.save(`order_delivery_history_report_${stamp()}.pdf`);
  };

  /* ---------- render ---------- */
  const anyFilter = activeKeys.length > 0 || globalTerms.length > 0 || selectedGroupConsignments.length > 0 || deliveryDateFrom || deliveryDateTo;

  return (
    <div className="ttr-page">
      {/* Filter Toolbar */}
      <div className="ttr-toolbar">
        <div className="ttr-title">Order Delivery History Report</div>
        
       <SearchableSelect
  options={groupConsignments.map((g) => g.grpConsignmentID)}
  value={selectedGroupConsignments}
  onChange={setSelectedGroupConsignments}
  placeholder="Search Group Consignment…"
  width={250}
/>

        <input
          className="ttr-search"
          placeholder="Search all columns… (separate several values with , or ;)"
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          onPaste={(e) => {
            const text = e.clipboardData.getData("text");
            if (/[\r\n\t]/.test(text)) {
              e.preventDefault();
              const joined = text.trim().split(/[\r\n\t]+/).join(", ");
              setGlobalSearch((prev) => (prev ? `${prev}, ${joined}` : joined));
            }
          }}
        />
        <div className="ttr-actions">
  <Button variant="primary" size="sm" onClick={fetchData} disabled={loading} title="Generate Report">
    <FontAwesomeIcon icon={faSync} spin={loading} className="me-1" />
    <span className="btn-label">Generate Report</span>
  </Button>
  <Button variant="outline-secondary" size="sm" onClick={clearAll} disabled={!anyFilter} title="Clear filters">
    <span className="btn-label">Clear filters{activeKeys.length ? ` (${activeKeys.length})` : ""}</span>
    <span className="d-sm-none">✕{activeKeys.length ? ` ${activeKeys.length}` : ""}</span>
  </Button>
  <Button variant="success" size="sm" onClick={exportToExcel} disabled={!filteredRows.length} title="Export Excel">
    <FontAwesomeIcon icon={faFileExcel} className="me-1" />
    <span className="btn-label">Export Excel</span>
  </Button>
  <Button variant="danger" size="sm" onClick={exportToPDF} disabled={!filteredRows.length} title="Export PDF">
    <FontAwesomeIcon icon={faFilePdf} className="me-1" />
    <span className="btn-label">Export PDF</span>
  </Button>
</div>
      </div>

      {/* Active filters */}
      {activeKeys.length > 0 && (
        <div className="ttr-active">
          <span className="ttr-active-label">Filters:</span>
          {activeKeys.map((k) => (
            <span className="ttr-chip" key={k} title={summarize(filters[k])}>
              <b>{COL_BY_KEY[k]?.label}</b>: {summarize(filters[k])}
              <button type="button" aria-label={`Remove ${COL_BY_KEY[k]?.label} filter`} onClick={() => setColFilter(k, {})}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="ttr-alert">
          {error}
        </Alert>
      )}

      {/* Grid */}
      <div className="ttr-grid-wrap">
        {loading && (
          <div className="ttr-loading">
            <Spinner animation="border" size="sm" /> Loading data...
          </div>
        )}
        <div className="ttr-grid-scroll" ref={scrollRef} onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}>
          <table className="erp-grid" style={{ width: totalWidth }}>
            <colgroup>
              {COLUMNS.map((c) => (
                <col key={c.key} style={{ width: widths[c.key] }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {COLUMNS.map((c) => {
                  const filtered = !!filters[c.key];
                  return (
                    <th
                      key={c.key}
                      className={`${c.key === "sr" ? "col-sr" : ""} ${filtered ? "filtered" : ""}`}
                      title={c.label}
                    >
                      <div className="th-inner">
                        <span className="th-label">{c.label}</span>
                        {c.filter !== "none" && (
                          <button
                            type="button"
                            data-filter-btn
                            className={`th-filter ${filtered ? "on" : ""}`}
                            title="Filter this column"
                            onClick={(e) => toggleFilter(e, c.key)}
                          >
                            <FontAwesomeIcon icon={faFilter} />
                          </button>
                        )}
                      </div>
                      <span
                        className="col-resizer"
                        onMouseDown={(e) => startResize(e, c.key)}
                        onDoubleClick={() => resetColumnWidth(c.key)}
                        title="Drag to resize · Double-click to reset"
                      />
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {total > 0 ? (
                <>
                  {topPad > 0 && (
                    <tr className="spacer" aria-hidden="true" style={{ height: topPad }}>
                      <td colSpan={COLUMNS.length} />
                    </tr>
                  )}
       {visibleRows.map((r, vi) => {
  const idx = start + vi;
  const prev = idx > 0 ? filteredRows[idx - 1] : null;
  const firstInGroup = !prev || prev.groupKey !== r.groupKey; // real start (for the border line)
  const firstRendered = vi === 0 || filteredRows[idx - 1].groupKey !== r.groupKey; // start inside the rendered window
  // rows covered by this merged cell, never beyond the rendered window
  const span = Math.min(groupEnd[idx], end - 1) - idx + 1;
  const cls = `${idx % 2 ? "alt" : ""} ${firstInGroup ? "grp-start" : ""}`;

  return (
    <tr key={r.id} className={cls}>
      {COLUMNS.map((c) => {
        // merged column: only the first rendered row of the group has the cell,
        // the rows below are covered by its rowSpan
        if (c.mergeGroup && !firstRendered) return null;

        return (
          <td
            key={c.key}
            className={`${c.key === "sr" ? "col-sr" : ""} ${c.mergeGroup ? "merged" : ""}`}
            style={{
              textAlign: c.align || "left",
              verticalAlign: c.mergeGroup ? "middle" : undefined,
            }}
            rowSpan={c.mergeGroup && span > 1 ? span : undefined}
            title={c.text(r, idx)}
          >
            {c.render ? c.render(r, idx) : c.text(r, idx) || "-"}
          </td>
        );
      })}
    </tr>
  );
})}
                  {bottomPad > 0 && (
                    <tr className="spacer" aria-hidden="true" style={{ height: bottomPad }}>
                      <td colSpan={COLUMNS.length} />
                    </tr>
                  )}
                </>
              ) : (
                !loading && (
                  <tr className="no-data-row">
                    <td colSpan={COLUMNS.length}>No data found for the selected filters</td>
                  </tr>
                )
              )}
            </tbody>
          </table>
        </div>
        <div className="ttr-statusbar">
          <span>
            Showing {total} of {rows.length} rows
          </span>
          <span>Click the funnel on a column header to filter · Drag a header edge to resize · Double-click the edge to reset</span>
        </div>
      </div>

      {openFilter &&
        createPortal(
          <FilterPopover
            key={openFilter.key}
            col={COL_BY_KEY[openFilter.key]}
            anchor={openFilter.rect}
            candidates={openCandidates}
            filter={filters[openFilter.key] || {}}
            onChange={(f) => setColFilter(openFilter.key, f)}
            onClear={() => setColFilter(openFilter.key, {})}
            onClose={closeFilter}
          />,
          document.body
        )}
    </div>
  );
};

export default OrderDeliveryHistoryReport;
